import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'lib-verify-email-confirmation',
  templateUrl: './verify-email-confirmation.component.html',
  styleUrls: ['./verify-email-confirmation.component.scss'],
})
export class VerifyEmailConfirmationComponent implements OnInit {
  response!: string;
  successfulResponse!: boolean;
  redirectCountdown = 5;
  redirectUrl = '/login';
  private appliedActionCode = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private angularFirebaseAuth: AngularFireAuth,
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      if (!this.isSupportedMode(params.mode)) {
        this.setInvalidLinkState();
        return;
      }

      const mode = params.mode as string;

      if (mode === 'verifyAndChangeEmail' && params.emailChangeComplete === '1') {
        this.response = 'Your email change has been confirmed successfully.';
        this.successfulResponse = true;
        this.updateRedirectCountdown();
        return;
      }

      const tenantId = params.tenantId as string | undefined;

      if (params.oobCode) {
        this.handleVerifyEmailWithOOBCode(params.oobCode, mode, tenantId);
        return;
      }

      if (params.verifyLink) {
        const decodedLink = atob(decodeURIComponent(params.verifyLink));
        const searchParams = this.getSearchParams(decodedLink);

        const oobCode = searchParams?.get('oobCode');
        const linkTenantId = searchParams?.get('tenantId') || tenantId;
        const linkMode = searchParams?.get('mode') || mode;

        this.handleVerifyEmailWithOOBCode(oobCode, linkMode, linkTenantId || undefined);
        return;
      }

      this.setInvalidLinkState();
    });
  }

  handleVerifyEmailWithOOBCode(actionCode: string | null, mode: string, tenantId?: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!actionCode) {
      this.setInvalidLinkState();
      return;
    }
    if (this.appliedActionCode) {
      return;
    }
    this.appliedActionCode = true;
    if (tenantId) {
      this.angularFirebaseAuth.tenantId = Promise.resolve(tenantId);
    }
    this.angularFirebaseAuth
      .applyActionCode(actionCode)
      .then((res) => {
        this.response =
          mode === 'verifyAndChangeEmail'
            ? 'Your email change has been confirmed successfully.'
            : 'Your email has been verified successfully.';
        this.successfulResponse = true;
        this.updateRedirectCountdown();
      })
      .catch((e) => {
        console.log(e);
        this.appliedActionCode = false;
        this.response = this.getVerificationErrorMessage(e);
        this.successfulResponse = false;
      });
  }

  private getSearchParams(decodedLink: string): URLSearchParams | null {
    try {
      return new URL(decodedLink).searchParams;
    } catch {
      const rawQuery = decodedLink.includes('?') ? decodedLink.split('?')[1] : decodedLink;
      return new URLSearchParams(rawQuery);
    }
  }

  private getVerificationErrorMessage(error: unknown): string {
    const code = (error as { code?: string })?.code || '';
    if (code.includes('expired-action-code')) {
      return 'This verification link has expired. Please log in again to request a new link.';
    }
    if (code.includes('invalid-action-code')) {
      return 'This verification link is invalid or has already been used. Please log in again to request a new link.';
    }
    if (code.includes('user-disabled')) {
      return 'This account has been disabled. Please contact support.';
    }
    return 'Unfortunately we were unable to verify your account. Please log in again to request a new verification code.';
  }

  private isSupportedMode(mode: unknown): mode is 'verifyEmail' | 'verifyAndChangeEmail' {
    return mode === 'verifyEmail' || mode === 'verifyAndChangeEmail';
  }

  private setInvalidLinkState() {
    this.response = 'Invalid verification link. Please log in again to request a new link.';
    this.successfulResponse = false;
  }

  handleVerifyEmailWithLink(link: string) {
    this.http
      .get(link)
      .toPromise()
      .then((res) => {
        this.response = ''; //'Your email has been verified successfully';
        this.successfulResponse = true;
        this.updateRedirectCountdown();
      })
      .catch((e) => {
        console.log(e);
        this.response =
          'Unfortunately we were unable to verify your account. The link you used may have expired, please try logging in again to request a new verification code.';
        this.successfulResponse = false;
      });
  }

  updateRedirectCountdown() {
    if (this.redirectCountdown > 0) {
      --this.redirectCountdown;
      setTimeout(() => {
        this.updateRedirectCountdown();
      }, 1000);
    } else {
      this.router.navigateByUrl(this.redirectUrl);
    }
  }
}
